import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Checkbox } from "@shared/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { ArrowRight, Lightbulb } from "lucide-react";
import React, { useState } from "react";

interface System {
  id: string;
  name: string;
  location: string;
  assets: number;
  conditionScore: number;
  riskScore: number;
}

interface SuggestedSystem {
  id: string;
  name: string;
  type: string;
  reason: string;
  reasonType: "facility-type" | "geographic";
}

interface SystemSearchResult {
  id: string;
  name: string;
  type: string;
  status: string;
  location: string;
  facilityName?: string;
}

interface Facility {
  name: string;
  type?: string;
}

interface FilterData {
  search: string;
  location: string;
  conditionScore: string;
  riskScore: string;
}

interface SearchFilterData {
  systemType: string;
  status: string;
}

const getConditionColor = (score: number) => {
  if (score >= 80) return "bg-green-100 text-green-800";
  if (score >= 60) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
};

const getRiskColor = (score: number) => {
  if (score <= 30) return "bg-green-100 text-green-800";
  if (score <= 60) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "Operational":
      return "bg-green-100 text-green-800";
    case "Standby":
      return "bg-yellow-100 text-yellow-800";
    case "Maintenance":
      return "bg-orange-100 text-orange-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const SystemSuggestionsModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  facilityName: string;
  facilityType: string;
  onConnectSuggestions: (systemIds: string[]) => void;
  onSkipToSearch: () => void;
}> = ({
  isOpen,
  onClose,
  facilityName,
  facilityType,
  onConnectSuggestions,
  onSkipToSearch,
}) => {
  const [selectedSystems, setSelectedSystems] = useState<string[]>([]);

  const suggestedSystems: SuggestedSystem[] = [
    {
      id: "SYS-101",
      name: "Chlorine Disinfection System",
      type: "Disinfection",
      reason: `Standard for ${facilityType}s`,
      reasonType: "facility-type",
    },
    {
      id: "SYS-102",
      name: "Coagulation-Flocculation Unit",
      type: "Treatment",
      reason: `Common in ${facilityType}s`,
      reasonType: "facility-type",
    },
    {
      id: "SYS-103",
      name: "Zone 3 Booster Pump System",
      type: "Pumping",
      reason: "Located within this facility's service area",
      reasonType: "geographic",
    },
    {
      id: "SYS-104",
      name: "Northwood Distribution Network",
      type: "Distribution",
      reason: "Geographic proximity to facility location",
      reasonType: "geographic",
    },
  ];

  const handleSystemToggle = (systemId: string) => {
    setSelectedSystems((prev) =>
      prev.includes(systemId)
        ? prev.filter((id) => id !== systemId)
        : [...prev, systemId]
    );
  };

  const handleConnectSelected = () => {
    if (selectedSystems.length > 0) {
      onConnectSuggestions(selectedSystems);
      setSelectedSystems([]);
    }
  };

  const getReasonIcon = (reasonType: string) => {
    return reasonType === "facility-type" ? "🏭" : "📍";
  };

  const getReasonColor = (reasonType: string) => {
    return reasonType === "facility-type"
      ? "bg-blue-100 text-blue-800"
      : "bg-green-100 text-green-800";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-xl font-semibold text-gray-900">
            System Suggestions for {facilityName}
          </DialogTitle>
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
            <Lightbulb className="h-4 w-4 text-blue-600" />
            <span>
              We've identified systems that are likely to belong to this
              facility based on type and location.
            </span>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-4">
            <h3 className="font-medium text-gray-900">Suggested Systems</h3>
            <div className="space-y-3">
              {suggestedSystems.map((system) => (
                <div
                  key={system.id}
                  className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Checkbox
                    id={system.id}
                    checked={selectedSystems.includes(system.id)}
                    onCheckedChange={() => handleSystemToggle(system.id)}
                    className="mt-1"
                  />
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center justify-between">
                      <label
                        htmlFor={system.id}
                        className="font-medium text-gray-900 cursor-pointer"
                      >
                        {system.name}
                      </label>
                      <Badge variant="secondary" className="text-xs">
                        {system.type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">
                        {getReasonIcon(system.reasonType)}
                      </span>
                      <Badge
                        variant="secondary"
                        className={`text-xs ${getReasonColor(
                          system.reasonType
                        )}`}
                      >
                        Reason: {system.reason}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600">ID: {system.id}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-3 pt-4 border-t">
            <Button
              onClick={handleConnectSelected}
              disabled={selectedSystems.length === 0}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Connect Selected Suggestions ({selectedSystems.length})
            </Button>

            <Button
              variant="ghost"
              onClick={onSkipToSearch}
              className="w-full text-blue-600 hover:text-blue-700 hover:bg-blue-50"
            >
              Skip and Search All Systems
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>

            <Button variant="outline" onClick={onClose} className="w-full">
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
